import { useState, useEffect, useRef, useCallback } from 'react';
import { loadCache, saveCache } from '../services/kioskCache';

/**
 * Offline-first stale-while-revalidate hook.
 *
 * Always enabled by default — every page benefits from caching, not just kiosk.
 *
 * Behaviour:
 *  - Reads cache immediately so the page renders without a spinner.
 *  - If the device is already offline and a cache entry exists, skips the
 *    network request entirely and sets fromCache = true.
 *  - Fetches fresh data in the background; updates cache on success.
 *  - On network failure, keeps showing cache with fromCache = true.
 *  - On network failure with no cache, sets error.
 *  - Listens for the 'online' event and auto-refetches when connectivity
 *    is restored.
 *  - Exposes `refetch` for manual retry.
 *
 * When disabled (enabled = false), returns inert state so the hook can be
 * called unconditionally without affecting callers that opt out.
 */
const useKioskData = (cacheKey, fetcher, { enabled = true } = {}) => {
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  // Read cache once at init so useState / loading get the right initial values.
  const initCache = enabled ? loadCache(cacheKey) : null;
  const [data, setData] = useState(initCache);
  const [loading, setLoading] = useState(enabled && !initCache);
  const [error, setError] = useState(null);
  const [fromCache, setFromCache] = useState(false);
  // Bumping this counter triggers a re-fetch inside the effect.
  const [fetchTick, setFetchTick] = useState(0);

  const refetch = useCallback(() => setFetchTick(t => t + 1), []);

  useEffect(() => {
    if (!enabled) return;

    const cached = loadCache(cacheKey);

    // Already offline and have cached data — serve it without a network attempt.
    if (!navigator.onLine && cached != null) {
      setData(cached);
      setFromCache(true);
      setLoading(false);
      return;
    }

    let cancelled = false;
    if (!cached) setLoading(true);

    const load = async () => {
      try {
        const fresh = await fetcherRef.current();
        if (cancelled) return;
        saveCache(cacheKey, fresh);
        setData(fresh);
        setFromCache(false);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        const fallback = loadCache(cacheKey);
        if (fallback != null) {
          setData(fallback);
          setFromCache(true);
          setError(null);
        } else {
          setError(err.message || 'Error de conexión');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
    // fetchTick is intentional — it lets refetch() trigger a new run.
    // fetcher is intentionally excluded — fetcherRef.current keeps it current.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, enabled, fetchTick]);

  // React to network status changes.
  useEffect(() => {
    if (!enabled) return;

    const handleOnline = () => refetch();
    const handleOffline = () => {
      // If we have cache, mark it as the source so the banner appears.
      const cached = loadCache(cacheKey);
      if (cached != null) setFromCache(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [cacheKey, enabled, refetch]);

  return { data, loading, error, fromCache, refetch };
};

export default useKioskData;
